import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Avatar, Button, Input, Space, Table, Tag, message } from "antd";
import { connect } from "react-redux";
import { getUserInfo } from "../../utils/redux/reducers/User";
import { ApiManager, Locale } from "../../utils";

const api = ApiManager.getInstance();
const i18n = Locale.getInstance();

const STATUS_REGISTERED = 1;
const STATUS_NOT_REGISTERED = 0;
const STATUS_EXPIRED = 2;

function personCell(person) {
  if (!person) {
    return "-";
  }
  return (
    <Space>
      {person.avatar ? <Avatar src={person.avatar} size={36} /> : <Avatar size={36} />}
      <div>
        <div>{person.nickname || "-"}</div>
        <div style={{ color: "#888" }}>
          {person.mobile || person.open_id || ""}
        </div>
      </div>
    </Space>
  );
}

function statusTag(status) {
  if (Number(status) === STATUS_REGISTERED) {
    return <Tag color="green">{i18n.t("proxy.recommenderList.statusRegistered")}</Tag>;
  }
  if (Number(status) === STATUS_EXPIRED) {
    return <Tag>{i18n.t("proxy.recommenderList.statusExpired")}</Tag>;
  }
  return <Tag color="orange">{i18n.t("proxy.recommenderList.statusNotRegistered")}</Tag>;
}

function AgentRecommenderList({ user }) {
  const [recommenderKeyword, setRecommenderKeyword] = useState("");
  const [appliedRecommender, setAppliedRecommender] = useState("");
  const [list, setList] = useState({ data: [], current_page: 1, total: 0 });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const fetchList = useCallback(
    async (nextPage = 1) => {
      const { token } = user || {};
      if (!token) return;

      setLoading(true);
      try {
        const query = {
          page: nextPage,
          count: pageSize,
        };
        if (appliedRecommender) {
          query["filter[recommender]"] = appliedRecommender;
        }

        const res = await api.proxy.bmallClients.run({ token, query });
        if (res.result) {
          setList({
            data: res.data?.data || [],
            current_page: res.data?.current_page || nextPage,
            total: res.data?.total || 0,
          });
          setPage(res.data?.current_page || nextPage);
        } else {
          message.warning(res.message);
        }
      } catch (error) {
        message.error(error.message || String(error));
      } finally {
        setLoading(false);
      }
    },
    [user, appliedRecommender]
  );

  useEffect(() => {
    fetchList(1);
  }, [fetchList]);

  const onSearch = () => {
    setAppliedRecommender(recommenderKeyword.trim());
  };

  const onReset = () => {
    setRecommenderKeyword("");
    setAppliedRecommender("");
  };

  const columns = useMemo(
    () => [
      {
        title: i18n.t("proxy.recommenderList.recommender"),
        key: "inviter",
        render: (_, row) => personCell(row.inviter),
      },
      {
        title: i18n.t("proxy.recommenderList.invitee"),
        key: "invitee",
        render: (_, row) => personCell(row.invitee),
      },
      {
        title: i18n.t("proxy.filterStatus"),
        key: "status",
        width: 120,
        render: (_, row) => statusTag(row.status),
      },
      {
        title: i18n.t("proxy.recommenderList.createdAt"),
        dataIndex: "created_at",
        key: "created_at",
        width: 170,
      },
      {
        title: i18n.t("proxy.recommenderList.expireDate"),
        dataIndex: "expire_date",
        key: "expire_date",
        width: 120,
        render: (value) => value || "-",
      },
    ],
    []
  );

  return (
    <div className="agent-recommender-list" style={{ padding: 16 }}>
      <Space wrap style={{ marginBottom: 16 }} size="middle">
        <div>
          <div style={{ marginBottom: 4, color: "#666", fontSize: 12 }}>
            {i18n.t("proxy.recommenderList.recommenderFilter")}
          </div>
          <Input
            style={{ width: 260 }}
            placeholder={i18n.t("proxy.recommenderList.recommenderHint")}
            value={recommenderKeyword}
            onChange={(e) => setRecommenderKeyword(e.target.value)}
            onPressEnter={onSearch}
            allowClear
          />
        </div>
        <div style={{ paddingTop: 18 }}>
          <Space>
            <Button type="primary" onClick={onSearch}>
              {i18n.t("common.search")}
            </Button>
            <Button onClick={onReset}>
              {i18n.t("proxy.recommenderList.reset")}
            </Button>
          </Space>
        </div>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={list.data || []}
        pagination={{
          current: page,
          total: list.total || 0,
          pageSize,
          showSizeChanger: false,
          onChange: (next) => fetchList(next),
        }}
      />
    </div>
  );
}

export default connect((state) => ({
  user: getUserInfo(state),
}))(AgentRecommenderList);
